import React from 'react';

// Material UI
import Typography from '@material-ui/core/Typography';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';

// Data
import dayjs from 'dayjs';
import { Box } from '@material-ui/core';
import { PrimaryButton } from '../../_core/_ui/buttons.component';
import { IntlMsg } from '../../_core/IntlMsg/intl-msg.component';
import { AssessmentSessionSummary } from '../../Session/assessment-session-summary.component';
import { messages } from './email-log-list.messages';
import { useCommunicationTypeLabel } from './email-log-list.utils';

const detailStyle = { color: '#666', fontSize: '0.9em' };

// Skipped, Pending and In Progress are not failures, so none prompts a retry:
// the address is gone, the send is queued, or a delivery already holds the row.
const RESEND_OUTCOME_MESSAGES: Partial<Record<ResendOutcome, string>> = {
  "Success": "Resent notification",
  "Skipped": "Not resent — this address is no longer a contact on the player",
  "Pending": "Already queued for delivery",
  "In Progress": "Not resent — a delivery of this message is already in progress",
};

interface ICommunicationList {
  data?: ICommunication[];
  loading: boolean
  // The outcome the send settled on; never rejects.
  resend: (_: string) => Promise<ResendOutcome>
  user: IUser | null;
}

export const CommunicationList: React.FC<ICommunicationList> = ({ data = [], loading, resend }) => {
  const communicationTypeLabel = useCommunicationTypeLabel()

  if (data?.length === 0 && loading) return <Typography>Loading...</Typography>;

  return (
    <List style={{ maxHeight: '400px', overflowY: 'auto' }}>
      {data?.map(log => {
        const deliveredAt = dayjs(log.scheduledAt || log.sentAt).format('YYYY/MM/DD h:mm A');
        const dateMessage = log.scheduledAt ? messages.scheduledAt : messages.sentAt;

        return (
          <ListItem key={log._id}>
            <Box display="flex" alignItems="center" justifyItems="space-between" width="100%">
              <ListItemText
                primary={log.communicationGroup?.title || communicationTypeLabel(log.type)}
                secondary={<span>
                  <span>{log.to}</span>
                  {(log.status === "Success" || log.status === "Pending") && <><br /><IntlMsg {...dateMessage} values={{ when: deliveredAt }} /></>}
                  {log.status !== "Success" && <><br /><span>{log.status}</span></>}
                  {log.assessmentSession && (
                    <>
                      <br />
                      <span style={detailStyle}>
                        <AssessmentSessionSummary session={log.assessmentSession} />
                      </span>
                    </>
                  )}
                </span>}
              />
              <Box>
                {/* `undefined` is "not asked", not "gone" — only an explicit
                    false, which the server proved, retires the button. */}
                {log.hasActiveRecipient === false ? (
                  <Typography variant="caption" style={detailStyle}>Contact removed</Typography>
                ) : (
                  <PrimaryButton
                    onClick={() => {
                      if (window.confirm("Are you sure you want to resend notification?")) {
                        resend(log._id)
                          .then((outcome) => alert(RESEND_OUTCOME_MESSAGES[outcome] || "Failed to resend notification"))
                      }
                    }
                  }>
                    Resend
                  </PrimaryButton>
                )}
              </Box>
            </Box>
          </ListItem>
        );
      })}
      {data.length === 0 && (
        <ListItem>
          <ListItemText primary="None" />
        </ListItem>
      )}
    </List>
  );
};
